import {
  formatSystemRole,
  buildSystemRolesTooltip,
  ROLE_DESCRIPTIONS,
  ROLE_CATEGORIES,
  RoleCategory,
  ROLE_INDICATOR_COLORS,
  ROLE_INDICATOR_ICONS,
} from './systemRolesFormatting';

import { LockOutlined } from '@ant-design/icons';
import React from 'react';
import { SystemRole } from 'shared/src/authz/rolesV2';

import type { BadgeDropdownProps, DropdownMenuGroup } from 'app/_components/BadgeDropdown/BadgeDropdown';

/**
 * Mapping of role categories to their display labels
 */
const ROLE_CATEGORY_LABELS: Record<RoleCategory, string> = {
  [RoleCategory.ADMINISTRATIVE]: 'ADMINISTRATIVE ROLES',
  [RoleCategory.STANDARD]: 'STANDARD ROLES',
  [RoleCategory.OPERATIONAL]: 'OPERATIONAL ROLES',
  [RoleCategory.SUPPORT]: 'SUPPORT ROLES',
};

/**
 * Define role priorities for display (highest priority shown in badge)
 */
export const ROLE_PRIORITY: Record<string, number> = {
  [SystemRole.ADMIN]: 1000,
  [SystemRole.ADMIN_READER]: 900,
  [SystemRole.ENTITLEMENTS_ADMIN]: 750,
  [SystemRole.ENTITLEMENTS_READER]: 700,
  [SystemRole.PURGE]: 800,
  [SystemRole.SMTP_ADMIN]: 800,
  [SystemRole.CICD]: 800,
  [SystemRole.SE]: 700,
  [SystemRole.BILLING_READER]: 700,
  [SystemRole.SUPPORT]: 700,
  [SystemRole.DEVELOPER]: 600,
  [SystemRole.USER]: 500,
};

/**
 * Get the highest priority role from the list of roles the *current user* possesses.
 * This is used to determine which role label to display on the badge.
 */
const getHighestPriorityRole = (roles: SystemRole[]): SystemRole => {
  if (!roles || roles.length === 0) {
    return SystemRole.USER; // Default if no roles
  }

  // Sort roles by their priority in descending order
  const sortedRoles = [...roles].sort((a, b) => {
    const priorityA = ROLE_PRIORITY[a] || 0;
    const priorityB = ROLE_PRIORITY[b] || 0;

    return priorityB - priorityA; // Descending order
  });

  return sortedRoles[0];
};

/**
 * Build dropdown menu items for system roles
 * Shows all available roles with indicators for assigned/unassigned
 * Hides implied base roles when higher roles exist
 */
const buildSystemRolesMenuItems = (userRoles: SystemRole[]): DropdownMenuGroup[] => {
  const userRolesSet = new Set(userRoles);
  const items: DropdownMenuGroup[] = [];

  // Order to display categories
  const categoryOrder = [
    RoleCategory.ADMINISTRATIVE,
    RoleCategory.STANDARD,
    RoleCategory.OPERATIONAL,
    RoleCategory.SUPPORT,
  ];

  // Helper to check if a role should be hidden due to a superseding role
  const shouldHideRole = (role: SystemRole): boolean => {
    // Administrative hierarchy: admin > admin_reader
    if (role === SystemRole.ADMIN_READER && userRolesSet.has(SystemRole.ADMIN)) {
      return true;
    }

    // Standard hierarchy: developer > user
    if (role === SystemRole.USER && userRolesSet.has(SystemRole.DEVELOPER)) {
      return true;
    }

    return false;
  };

  // Iterate through categories in order
  for (const category of categoryOrder) {
    const categoryRoles = ROLE_CATEGORIES[category];

    if (!categoryRoles) {
      continue;
    }

    const visibleRoles = categoryRoles.filter((role) => !shouldHideRole(role));

    // Only add group if there are visible roles
    if (visibleRoles.length === 0) {
      continue;
    }

    items.push({
      label: ROLE_CATEGORY_LABELS[category],
      children: visibleRoles.map((role) => {
        const hasRole = userRolesSet.has(role);

        return {
          label: formatSystemRole(role),
          key: role,
          description: ROLE_DESCRIPTIONS[role],
          icon: hasRole ? ROLE_INDICATOR_ICONS.ASSIGNED : ROLE_INDICATOR_ICONS.UNASSIGNED,
          iconColor: hasRole ? ROLE_INDICATOR_COLORS.ASSIGNED : ROLE_INDICATOR_COLORS.UNASSIGNED,
        };
      }),
    });
  }

  // Sort categories: groups with user-enabled roles appear first
  items.sort((a, b) => {
    const groupA = a.children || [];
    const groupB = b.children || [];

    const userHasRoleInA = groupA.some((item) => userRolesSet.has(item.key as SystemRole));
    const userHasRoleInB = groupB.some((item) => userRolesSet.has(item.key as SystemRole));

    // Both have roles or both don't: maintain order
    if (userHasRoleInA === userHasRoleInB) {
      return 0;
    }

    // A has roles, B doesn't: A comes first
    return userHasRoleInA ? -1 : 1;
  });

  return items;
};

/**
 * Helper function to prepare system roles data for BadgeDropdown component
 * Handles formatting, grouping, prioritization, and styling
 *
 * Returns null if user has no roles (component should not render)
 */
export const buildSystemRolesBadgeDropdown = ({
  userRoles,
}: {
  userRoles: SystemRole[];
}): BadgeDropdownProps | null => {
  // Don't render if user has no roles
  if (!userRoles || userRoles.length === 0) {
    return null;
  }

  const highestRole = getHighestPriorityRole(userRoles);
  // Pass user's roles to show all available roles with indicators
  const menuItems = buildSystemRolesMenuItems(userRoles);
  const tooltipTitle = buildSystemRolesTooltip(userRoles);

  // Add footer note indicating visibility is limited to Cribl employees
  const menuItemsWithFooter: BadgeDropdownProps['menuItems'] = [
    ...(menuItems || []),
    {
      label: '',
      children: [
        {
          label: '',
          key: '_internal_note',
          description: 'This badge is only visible to @cribl.io accounts',
        },
      ],
      isFooter: true,
    },
  ];

  return {
    icon: React.createElement(LockOutlined),
    label: formatSystemRole(highestRole),
    count: userRoles.length > 1 ? userRoles.length - 1 : 0,
    tooltipTitle,
    menuItems: menuItemsWithFooter,
  };
};
